/**
 * Account Information Page Component
 *
 * This page displays the user's account settings and information.
 * It provides functionality for viewing user details and changing passwords.
 *
 * Features:
 * - Display current user information
 * - User profile details management
 * - Password change functionality
 * - Loading state with skeleton while user data is fetching
 */

import { Grid, Skeleton, Stack, Typography } from "@mui/material";

import { useGetCurrentUser, useGetClientWithIds, useGetOrganisationWithIds } from "../../store";

import UserDetailsCard from "./components/user-details-card";
import ChangePasswordCard from "./components/change-password-card";
import MainLayout from "../../layouts/main-layout";
import EmailPreferencesCard from "./components/email-preferences-card";
import ClientsCard from "./components/clients-card"
import TeamsCard from "./components/teams-card";

const AccountInfoPage: React.FC = () => {
  // Fetch current user data from the store
  const { data: currentUser } = useGetCurrentUser();
  const clientIds = currentUser?.allClientIds ?? [];
  const organisationIds = currentUser?.organisationIds ?? [];

  const { data: clients = [] } = useGetClientWithIds(clientIds);
  const { data: teams = [] } = useGetOrganisationWithIds(
    organisationIds,
  );

  return (
    <MainLayout>
      {/* Main content when user data is loaded */}
      {currentUser && (
        <Stack spacing={2} marginBottom={4}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h3">Account Settings</Typography>
            <Typography variant="body2" color="text.secondary">
              Last Login:{" "}
              {currentUser.lastLogin
                ? `${new Date(currentUser.lastLogin).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }).replace(/,/g, "")}, ${new Date(currentUser.lastLogin).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true })}`
                : "unknown"}
            </Typography>
          </Stack>

          {/* Two-column layout for account components */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Left column: User details and profile management */}
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <UserDetailsCard user={currentUser} />
            </Grid>

            {/* Right column: Password change functionality */}
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <ChangePasswordCard />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <Stack spacing={2}>
                <EmailPreferencesCard user={currentUser} />
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <ClientsCard user={currentUser} clients={clients} />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <TeamsCard
                user={currentUser}
                teams={teams?.filter((team) => team !== null) ?? []}
              />
            </Grid>
          </Grid>
        </Stack>
      )}

      {/* Loading state skeleton while user data is being fetched */}
      {!currentUser && (
        <Skeleton variant="rectangular" width={300} height={300} />
      )}
    </MainLayout>
  );
};

export default AccountInfoPage;
